import React, { useState } from "react";
import { motion } from "framer-motion";
import { Sparkles, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { TID } from "@/constants/testIds";
import { toast } from "sonner";

const INTERESTS = ["Surf", "Fine dining", "Family", "Wellness", "Nightlife", "Hinterland", "Photography", "Luxury"];
const GROUPS = [
  { k: "solo", l: "Solo" },
  { k: "couple", l: "Couple" },
  { k: "family", l: "Family" },
  { k: "friends", l: "Friends" },
  { k: "corporate", l: "Corporate" },
];
const BUDGETS = [
  { k: "budget", l: "Budget" },
  { k: "mid", l: "Mid-range" },
  { k: "luxe", l: "Luxe" },
];

export default function TripPlanner() {
  const [form, setForm] = useState({
    start_date: "",
    end_date: "",
    budget: "mid",
    group: "couple",
    interests: [],
    notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [itinerary, setItinerary] = useState("");

  const toggle = (k) => setForm((f) => ({
    ...f,
    interests: f.interests.includes(k) ? f.interests.filter((x) => x !== k) : [...f.interests, k],
  }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.start_date || !form.end_date) return toast.error("Add your dates");
    setBusy(true);
    setItinerary("");
    try {
      const { data } = await api.post("/concierge/trip-plan", form);
      setItinerary(data.itinerary);
      setTimeout(() => document.getElementById("itinerary-result")?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch {
      toast.error("Couldn't generate itinerary. Try again.");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-[1100px] mx-auto px-6 lg:px-10 pt-16 pb-24">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <span className="editorial-divider" />
        <h1 className="font-display text-5xl md:text-7xl mt-4 tracking-tight">Trip Planner.</h1>
        <p className="text-lg text-[color:var(--forest-2)] mt-4 max-w-2xl">
          Tell us the shape of your trip. We'll draft a day-by-day itinerary from the coast's best.
        </p>
      </motion.div>

      <form onSubmit={submit} data-testid={TID.tripPlannerForm} className="mt-12 bg-white rounded-3xl border border-[color:var(--forest-line)] p-8 md:p-12">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div>
            <label className="text-xs uppercase tracking-[0.22em] text-[color:var(--forest-2)] block mb-3">Arrival</label>
            <input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                   className="w-full bg-white border border-[color:var(--forest-line)] rounded-full px-5 py-3 outline-none focus:border-[color:var(--terracotta)]" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.22em] text-[color:var(--forest-2)] block mb-3">Departure</label>
            <input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                   className="w-full bg-white border border-[color:var(--forest-line)] rounded-full px-5 py-3 outline-none focus:border-[color:var(--terracotta)]" />
          </div>
        </div>

        <div className="mt-8">
          <label className="text-xs uppercase tracking-[0.22em] text-[color:var(--forest-2)] block mb-3">Who is travelling?</label>
          <div className="flex flex-wrap gap-2">
            {GROUPS.map((g) => (
              <button type="button" key={g.k} onClick={() => setForm({ ...form, group: g.k })}
                      className={`chip ${form.group === g.k ? "chip-active" : ""}`}>{g.l}</button>
            ))}
          </div>
        </div>

        <div className="mt-8">
          <label className="text-xs uppercase tracking-[0.22em] text-[color:var(--forest-2)] block mb-3">Budget</label>
          <div className="flex flex-wrap gap-2">
            {BUDGETS.map((g) => (
              <button type="button" key={g.k} onClick={() => setForm({ ...form, budget: g.k })}
                      className={`chip ${form.budget === g.k ? "chip-active" : ""}`}>{g.l}</button>
            ))}
          </div>
        </div>

        <div className="mt-8">
          <label className="text-xs uppercase tracking-[0.22em] text-[color:var(--forest-2)] block mb-3">Interests</label>
          <div className="flex flex-wrap gap-2">
            {INTERESTS.map((k) => (
              <button type="button" key={k} onClick={() => toggle(k)}
                      className={`chip ${form.interests.includes(k) ? "chip-active" : ""}`}>{k}</button>
            ))}
          </div>
        </div>

        <div className="mt-8">
          <label className="text-xs uppercase tracking-[0.22em] text-[color:var(--forest-2)] block mb-3">Anything else?</label>
          <textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    placeholder="Anniversary, dietary needs, mobility, must-see venues…"
                    className="w-full bg-white border border-[color:var(--forest-line)] rounded-2xl px-5 py-3 outline-none focus:border-[color:var(--terracotta)] resize-none" />
        </div>

        <div className="mt-10 flex justify-end">
          <button type="submit" disabled={busy} data-testid={TID.tripPlannerSubmit}
                  className="btn-pill btn-primary text-base py-4 px-8 disabled:opacity-40">
            <Sparkles size={16} /> {busy ? "Drafting…" : "Generate itinerary"} <ArrowRight size={15} />
          </button>
        </div>
      </form>

      {itinerary && (
        <motion.div
          id="itinerary-result"
          data-testid={TID.tripPlannerResult}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-14 bg-[color:var(--forest)] text-[color:var(--bone)] rounded-3xl p-8 md:p-12 relative overflow-hidden grain"
        >
          <div className="relative z-10">
            <div className="text-xs uppercase tracking-[0.3em] opacity-70">Your itinerary</div>
            <h2 className="font-display text-3xl md:text-4xl mt-2 mb-6">Curated by Hey GC</h2>
            <div className="prose-heygc !text-[color:var(--bone)] whitespace-pre-line leading-relaxed">
              {itinerary}
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
}
